/**
 * Market Map View Page
 * Trang /map với dynamic tabs từ database
 * Hỗ trợ fullscreen mode với auto-hide sidebar/detail
 * Mobile: Bottom sheet pattern cho sidebar
 * Hỗ trợ URL params để share link: /map/:mapId
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { HiOutlineMap, HiOutlineMenuAlt2, HiOutlineX, HiChevronUp, HiLocationMarker } from 'react-icons/hi'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import {
  MarketMapSidebar,
  MarketMapDetailPanel,
  MarketMapLeaflet,
  CompareTab,
  MobileMarkerList,
} from '../components/market-map'
import marketMapService from '../services/marketMapService'
import BottomSheet from '../components/common/BottomSheet'
import SEO from '../components/common/SEO'
import MapAccessDenied from '../components/common/MapAccessDenied'

// Helper to get default map based on settings
const getDefaultMap = (settings, maps) => {
  const defaultTab = settings?.default_tab || 'cho_thue'

  // First, try to use specific default map ID for the tab
  const specificMapId = defaultTab === 'cho_thue'
    ? settings?.default_map_cho_thue
    : settings?.default_map_mua_ban

  if (specificMapId) {
    const specificMap = maps.find(m => m.id === specificMapId)
    if (specificMap) return specificMap
  }

  // Fallback: find first active map of the default tab type
  const mapOfType = maps.find(m => m.map_type === defaultTab)
  if (mapOfType) return mapOfType

  // Last fallback: first map in list
  return maps[0]
}

function MarketMapView() {
  const { mapId: urlMapId } = useParams()
  const navigate = useNavigate()
  // Dynamic tabs from database
  const [availableMaps, setAvailableMaps] = useState([])
  const [activeMapId, setActiveMapId] = useState(null)
  const [isCompareTab, setIsCompareTab] = useState(false)

  const [loading, setLoading] = useState(true)
  const [mapData, setMapData] = useState(null)
  const [markers, setMarkers] = useState([])
  const [utilityMarkers, setUtilityMarkers] = useState([]) // Utility markers (schools, hospitals, etc.)
  const [paths, setPaths] = useState([]) // NEW: paths/shapes
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [showDetailPanel, setShowDetailPanel] = useState(false)
  const [settings, setSettings] = useState({})
  const [accessDenied, setAccessDenied] = useState(null) // null | { level: 'internal' | 'private', message: string }

  // Fullscreen mode state
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showSidebarInFullscreen, setShowSidebarInFullscreen] = useState(false)
  const fullscreenContainerRef = useRef(null)

  // State for bottom sheet snap (must be before any early returns)
  const [bottomSheetSnap, setBottomSheetSnap] = useState('collapsed')

  // Load all active maps on mount
  useEffect(() => {
    const init = async () => {
      try {
        // Load settings
        const settingsRes = await marketMapService.getSettings()
        if (settingsRes.status === 'success' && settingsRes.data) {
          setSettings(settingsRes.data)
        }

        // Load all active maps
        const mapsRes = await marketMapService.getAllActiveMaps()
        if (mapsRes.status === 'success' && mapsRes.data) {
          const maps = mapsRes.data.maps || []
          setAvailableMaps(maps)

          if (maps.length > 0) {
            // Check if URL has a mapId
            if (urlMapId) {
              const targetMapId = parseInt(urlMapId, 10)
              const targetMap = maps.find(m => m.id === targetMapId)
              if (targetMap) {
                // Load map from URL (map is in available maps list)
                setActiveMapId(targetMapId)
                loadMapById(targetMapId)
              } else {
                // Map not in available list - could be private or not exist
                // Try to load it directly to get proper error (403 for private)
                setActiveMapId(targetMapId)
                try {
                  const mapRes = await marketMapService.getMapById(targetMapId)
                  if (mapRes.status === 'success' && mapRes.data) {
                    // Map loaded successfully (shouldn't happen if not in list)
                    setMapData(mapRes.data)
                    setMarkers(mapRes.data.markers || [])
                    setLoading(false)
                  }
                } catch (err) {
                  // Check if 403 (restricted access - internal or private)
                  if (err.response?.status === 403 || err.response?.data?.code === 'RESTRICTED_MAP' || err.response?.data?.code === 'PRIVATE_MAP') {
                    setAccessDenied({
                      level: err.response?.data?.access_level || 'private',
                      message: err.response?.data?.message || 'Bản đồ này yêu cầu đăng nhập để xem'
                    })
                    setLoading(false)
                  } else {
                    // Not found - fallback to default
                    const defaultMap = getDefaultMap(settingsRes.data, maps)
                    setActiveMapId(defaultMap.id)
                    loadMapById(defaultMap.id)
                    navigate(`/map/${defaultMap.id}`, { replace: true })
                  }
                }
              }
            } else {
              // No mapId in URL, use default from settings
              const defaultMap = getDefaultMap(settingsRes.data, maps)
              setActiveMapId(defaultMap.id)
              loadMapById(defaultMap.id)
              // Update URL to include mapId
              navigate(`/map/${defaultMap.id}`, { replace: true })
            }
          } else {
            setLoading(false)
          }
        }
      } catch (error) {
        console.error('Failed to load maps:', error)
        setLoading(false)
      }
    }
    init()
  }, [urlMapId])

  // Handle fullscreen change from MarketMapLeaflet
  const handleFullscreenChange = useCallback((isNowFullscreen) => {
    setIsFullscreen(isNowFullscreen)

    // Khi vào fullscreen, mặc định hiển thị sidebar, chỉ ẩn detail panel
    if (isNowFullscreen) {
      setShowSidebarInFullscreen(true)  // Mặc định hiển thị sidebar
      setShowDetailPanel(false)          // Ẩn detail panel
    }
  }, [])

  const loadMapById = async (mapId) => {
    if (!mapId) return

    setLoading(true)
    setMarkers([])
    setUtilityMarkers([])
    setPaths([])
    setMapData(null)
    setSelectedMarker(null)
    setShowDetailPanel(false)
    setAccessDenied(null)

    try {
      const response = await marketMapService.getMapById(mapId)
      if (response.status === 'success' && response.data) {
        setMapData(response.data)
        setMarkers(response.data.markers || [])
        setUtilityMarkers(response.data.utility_markers || [])

        // Load paths from layers
        const allPaths = []
        const layers = response.data.layers || []
        layers.forEach(layer => {
          const shapes = layer.shapes || []
          shapes.forEach(shape => {
            allPaths.push({
              id: shape.id,
              name: shape.name,
              geojson: shape.geojson,
              shape_type: shape.shape_type,
              stroke_color: shape.style?.stroke_color || shape.stroke_color,
              fill_color: shape.style?.fill_color || shape.fill_color,
              stroke_width: shape.style?.stroke_width || shape.stroke_width || 3,
              stroke_opacity: shape.style?.stroke_opacity || shape.stroke_opacity || 1,
              fill_opacity: shape.style?.fill_opacity || shape.fill_opacity || 0.3,
              stroke_style: shape.style?.stroke_style || shape.stroke_style || 'solid',
              animation: shape.style?.animation || shape.animation || 'none',
              show_label: shape.show_label !== false, // Default true
            })
          })
        })
        setPaths(allPaths)
      }
    } catch (error) {
      console.error('Failed to load map:', error)
      // Check if it's a 403 (restricted access)
      if (error.response?.status === 403 || error.response?.data?.code === 'RESTRICTED_MAP' || error.response?.data?.code === 'PRIVATE_MAP') {
        setAccessDenied({
          level: error.response?.data?.access_level || 'private',
          message: error.response?.data?.message || 'Bản đồ này yêu cầu đăng nhập để xem'
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleMapChange = (mapId) => {
    if (mapId === 'so_sanh') {
      setIsCompareTab(true)
      setActiveMapId(null)
      // Update URL for compare tab
      navigate('/map', { replace: true })
    } else {
      setIsCompareTab(false)
      setActiveMapId(mapId)
      loadMapById(mapId)
      // Update URL to reflect selected map
      navigate(`/map/${mapId}`, { replace: true })
    }
    setSelectedMarker(null)
    setShowDetailPanel(false)
  }

  // Click marker: only select + pan (no popup)
  const handleMarkerClick = useCallback((marker) => {
    setSelectedMarker(marker)
  }, [])

  // Click "Xem chi tiet" button: show popup
  const handleViewDetail = useCallback((marker) => {
    setSelectedMarker(marker)
    setShowDetailPanel(true)
  }, [])

  const handleCloseDetailPanel = useCallback(() => {
    setShowDetailPanel(false)
  }, [])

  // Toggle sidebar trong fullscreen mode
  const toggleSidebarInFullscreen = useCallback(() => {
    setShowSidebarInFullscreen(prev => !prev)
  }, [])

  // Handle go back from access denied - navigate to default map
  const handleGoBackFromAccessDenied = useCallback(() => {
    setAccessDenied(null)
    if (availableMaps.length > 0) {
      const defaultMap = getDefaultMap(settings, availableMaps)
      setActiveMapId(defaultMap.id)
      loadMapById(defaultMap.id)
      navigate(`/map/${defaultMap.id}`, { replace: true })
    } else {
      navigate('/map', { replace: true })
    }
  }, [availableMaps, settings, navigate])

  // SEO data based on current map
  const seoData = useMemo(() => {
    if (isCompareTab) {
      return {
        title: 'So sánh bản đồ giá BĐS',
        description: 'So sánh giá bất động sản giữa các khu vực tại miền Trung Việt Nam',
        url: '/map',
      }
    }
    if (mapData) {
      return {
        title: mapData.name,
        description: mapData.description || `Bản đồ giá ${mapData.name} - Cập nhật mới nhất từ Đất Xanh Miền Trung`,
        url: `/map/${mapData.id}`,
      }
    }
    return {
      title: 'Bản đồ giá BĐS miền Trung',
      description: 'Bản đồ ma trận giá bất động sản các khu vực Đà Nẵng, Huế, Quy Nhơn và miền Trung Việt Nam',
      url: '/map',
    }
  }, [mapData, isCompareTab])

  // Handle bottom sheet snap change (must be before early returns)
  const handleSnapChange = useCallback((snap) => {
    setBottomSheetSnap(snap)
  }, [])

  // If access denied (restricted map)
  if (accessDenied) {
    const isPrivate = accessDenied.level === 'private'
    return (
      <>
        <SEO
          title={isPrivate ? 'Bản đồ riêng tư' : 'Bản đồ nội bộ'}
          description={accessDenied.message}
          url="/map"
        />
        <MapAccessDenied
          onGoBack={handleGoBackFromAccessDenied}
          accessLevel={accessDenied.level}
          message={accessDenied.message}
        />
      </>
    )
  }

  // If Compare tab is active
  if (isCompareTab) {
    return (
      <div className="flex h-[calc(100vh-64px)]">
        <SEO
          title={seoData.title}
          description={seoData.description}
          url={seoData.url}
        />
        <MarketMapSidebar
          availableMaps={availableMaps}
          activeMapId={null}
          isCompareTab={true}
          onMapChange={handleMapChange}
          markers={[]}
          selectedMarker={null}
          onMarkerClick={() => {}}
          mapInfo={null}
          loading={false}
          settings={settings}
        />

        <div className="flex-1 overflow-hidden">
          <CompareTab />
        </div>
      </div>
    )
  }

  // Determine if sidebar should be visible (desktop only)
  const sidebarVisible = !isFullscreen || showSidebarInFullscreen

  // Peek content for mobile bottom sheet
  const mobileSheetPeek = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
          <HiLocationMarker className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <span className="font-semibold text-gray-900">
            {markers.length} địa điểm
          </span>
          {selectedMarker && (
            <p className="text-xs text-gray-500 truncate max-w-[calc(100vw-180px)]">
              {selectedMarker.name}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 text-emerald-600">
        <span className="text-sm font-medium">Xem danh sách</span>
        <HiChevronUp className="w-4 h-4" />
      </div>
    </div>
  )

  return (
    <div
      ref={fullscreenContainerRef}
      className={clsx(
        'flex bg-gray-900 relative',
        isFullscreen ? 'h-screen' : 'h-[calc(100vh-64px)] md:h-[calc(100vh-64px)]'
      )}
    >
      <SEO
        title={seoData.title}
        description={seoData.description}
        url={seoData.url}
      />

      {/* Desktop Sidebar */}
      <div className={clsx(
        'hidden md:block transition-all duration-300 ease-in-out flex-shrink-0',
        sidebarVisible ? 'w-80' : 'w-0 overflow-hidden'
      )}>
        <MarketMapSidebar
          availableMaps={availableMaps}
          activeMapId={activeMapId}
          isCompareTab={false}
          onMapChange={handleMapChange}
          markers={markers}
          selectedMarker={selectedMarker}
          onMarkerClick={handleMarkerClick}
          onViewDetail={handleViewDetail}
          mapInfo={mapData}
          loading={loading}
          settings={settings}
        />
      </div>

      {/* Main Map Area - Full width on mobile */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <div className="animate-spin w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-gray-500">Đang tải bản đồ...</p>
            </div>
          </div>
        ) : mapData ? (
          <MarketMapLeaflet
            markers={markers}
            utilityMarkers={utilityMarkers}
            paths={paths}
            selectedMarker={selectedMarker}
            onMarkerClick={handleMarkerClick}
            onViewDetail={handleViewDetail}
            onFullscreenChange={handleFullscreenChange}
            fullscreenTargetRef={fullscreenContainerRef}
            center={{
              lat: mapData.center_lat || 16.0544,
              lng: mapData.center_lng || 108.2022,
            }}
            zoom={mapData.zoom_level || 13}
            mapStyle={mapData.map_style || 'hybrid'}
            useCustomCenter={!!(mapData.center_lat && mapData.center_lng)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <div className="text-center text-gray-400">
              <HiOutlineMap className="w-16 h-16 mx-auto mb-3" />
              <p className="text-lg font-medium">Chưa có bản đồ</p>
              <p className="text-sm mt-1">
                Admin chưa tạo bản đồ nào
              </p>
            </div>
          </div>
        )}

        {/* Ghi chú góc phải-dưới (admin bật trong cài đặt bản đồ) */}
        {mapData && mapData.show_note && mapData.note_text && (
          <div className="absolute right-3 z-[1100] bottom-[calc(13vh_+_64px)] md:bottom-7 max-w-[260px] sm:max-w-xs pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-md px-3 py-2 text-2xs sm:text-xs text-gray-600 leading-snug">
              {mapData.note_text}
            </div>
          </div>
        )}

        {/* Desktop: Fullscreen Toggle Sidebar Button */}
        {isFullscreen && (
          <button
            onClick={toggleSidebarInFullscreen}
            className={clsx(
              'hidden md:flex absolute top-4 z-20 p-3 rounded-xl shadow-lg transition-all',
              showSidebarInFullscreen
                ? 'left-[calc(320px+16px)] bg-gray-900 text-white hover:bg-gray-800'
                : 'left-4 bg-white text-gray-700 hover:bg-gray-100'
            )}
            title={showSidebarInFullscreen ? 'Ẩn danh sách' : 'Hiện danh sách'}
          >
            {showSidebarInFullscreen ? (
              <HiOutlineX className="w-5 h-5" />
            ) : (
              <HiOutlineMenuAlt2 className="w-5 h-5" />
            )}
          </button>
        )}

        {/* Detail Panel */}
        <MarketMapDetailPanel
          marker={selectedMarker}
          onClose={handleCloseDetailPanel}
          isOpen={showDetailPanel}
          isFullscreen={isFullscreen}
        />
      </div>

      {/* Mobile Bottom Sheet - Optimized mobile list */}
      <BottomSheet
        isOpen={!loading && !!mapData}
        peekContent={mobileSheetPeek}
        onSnapChange={handleSnapChange}
      >
        <MobileMarkerList
          markers={markers}
          selectedMarker={selectedMarker}
          onMarkerClick={handleMarkerClick}
          onViewDetail={handleViewDetail}
          mapInfo={mapData}
          loading={loading}
          availableMaps={availableMaps}
          activeMapId={activeMapId}
          onMapChange={handleMapChange}
        />
      </BottomSheet>
    </div>
  )
}

export default MarketMapView
